import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
    getMembers,
    inviteMember, // 이제 "역할 조정(추가/변경)" 용도로 사용
    removeMember,
    updateMemberRole,
    listProjectRoles,
    listCompanyUsers,
} from './api';
import type { Member, CompanyUserLite } from './types';
import type { Role } from '../admin/api';
import { useAuth } from '../auth/AuthContext';

// 이 UI에서 직접 부여/변경할 수 없는 고급 프로젝트 역할
const HIGH_PRIV_PROJECT_ROLES = ['PROJECT_OWNER', 'PROJECT_MANAGER'] as const;

type MembersTabProps = {
    projectId: string;
};

const MembersTab: React.FC<MembersTabProps> = ({ projectId }) => {
    const [items, setItems] = useState<Member[]>([]);
    const [projectRoles, setProjectRoles] = useState<Role[]>([]);
    const [email, setEmail] = useState('');
    const [role, setRole] = useState<string>(''); // 선택된 프로젝트 역할
    const [err, setErr] = useState<string | null>(null);

    // 회사 사용자 / 자동완성 & 모달 상태
    const [companyUsers, setCompanyUsers] = useState<CompanyUserLite[]>([]);
    const [companyUsersLoading, setCompanyUsersLoading] = useState(false);
    const [companyUsersErr, setCompanyUsersErr] = useState<string | null>(null);
    const [showUserPicker, setShowUserPicker] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [userFilter, setUserFilter] = useState(''); // 모달 검색용

    const auth = useAuth();

    // 현재 로그인 유저의 이 프로젝트에서의 역할
    const myProjectRole: Member['role'] | null = auth?.me
        ? items.find((m) => m.userId === auth.me?.id)?.role ?? null
        : null;

    const isOwnerInThisProject = myProjectRole === 'PROJECT_OWNER';
    const isManagerInThisProject = myProjectRole === 'PROJECT_MANAGER';
    const canManage = isOwnerInThisProject || isManagerInThisProject;

    // 멤버 목록 로드
    const reload = async () => {
        try {
            setErr(null);
            const r = await getMembers(projectId);
            const data = (r as any)?.data ?? r;
            setItems(Array.isArray(data) ? data : []);
        } catch (e: any) {
            setErr(e?.response?.data?.error?.message ?? '멤버 목록을 불러오지 못했습니다.');
        }
    };

    // 프로젝트 역할 목록 로드
    const loadRoles = async () => {
        try {
            const projRoles = await listProjectRoles(projectId);
            setProjectRoles(projRoles);

            const assignable = projRoles.filter((r) => !HIGH_PRIV_PROJECT_ROLES.includes(r.name as any));

            if (assignable.length > 0 && !assignable.some((r) => r.name === role)) {
                setRole(assignable[0].name);
            }
        } catch (e) {
            // roles 로딩 실패는 치명적이지 않으므로 콘솔만
            // eslint-disable-next-line no-console
            console.error('loadRoles failed', e);
        }
    };

    // 회사 사용자 미리 로드 (페이지 진입 시 한 번)
    useEffect(() => {
        const loadCompanyUsersOnce = async () => {
            try {
                setCompanyUsersErr(null);
                setCompanyUsersLoading(true);
                const users = await listCompanyUsers(projectId);
                setCompanyUsers(users);
            } catch (err) {
                console.error('listCompanyUsers failed: ', err);
                setCompanyUsersErr('회사 사용자 목록을 불러오지 못했습니다.');
            } finally {
                setCompanyUsersLoading(false);
            }
        };
        loadCompanyUsersOnce();
    }, [projectId]);

    useEffect(() => {
        reload();
        loadRoles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    // 공통 필터 함수: 이름/이메일 기준
    const filterCompanyUsers = useCallback(
        (query: string): CompanyUserLite[] => {
            const q = query.trim().toLowerCase();
            if (!q) return companyUsers;

            return companyUsers.filter((u) => {
                const name = (u.name ?? '').toLowerCase();
                const emailLower = u.email.toLowerCase();
                return name.includes(q) || emailLower.includes(q);
            });
        },
        [companyUsers],
    );

    // 자동완성용 필터 (email 인풋 기준)
    const filteredSuggestions = useMemo(() => {
        if (!showSuggestions) return [];
        return filterCompanyUsers(email);
    }, [showSuggestions, email, filterCompanyUsers]);

    // 모달용 필터 (userFilter 기준)
    const filteredCompanyUsers = useMemo(() => filterCompanyUsers(userFilter), [userFilter, filterCompanyUsers]);

    // ✅ 프로젝트 역할 조정 (기존 inviteMember 사용, 서버는 addOrUpdateMember로 동작)
    const handleInviteOrAdjust = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canManage) {
            setErr('이 프로젝트의 역할을 조정할 권한이 없습니다.');
            return;
        }

        const trimmedEmail = email.trim();
        if (!trimmedEmail) {
            setErr('이메일을 입력해 주세요.');
            return;
        }
        if (!role) {
            setErr('역할을 선택해 주세요.');
            return;
        }

        try {
            setErr(null);
            await inviteMember(projectId, { email: trimmedEmail, role: role as Member['role'] });

            setEmail('');

            const assignable = projectRoles.filter((r) => !HIGH_PRIV_PROJECT_ROLES.includes(r.name as any));
            if (assignable.length > 0) {
                setRole(assignable[0].name);
            }

            await reload();
        } catch (e: any) {
            const errData = e?.response?.data?.error;
            const code = errData?.code;
            const serverMsg = errData?.message;

            if (code === 'USER_NOT_FOUND') {
                setErr(
                    '해당 이메일로 가입된 사용자가 없습니다. 먼저 Company Admin이 회사 사용자(CompanyUser)로 추가해야 합니다.',
                );
            } else if (code === 'NOT_COMPANY_MEMBER') {
                setErr(
                    '해당 사용자는 이 프로젝트 회사의 구성원이 아닙니다. Company Admin이 회사 사용자로 먼저 등록해야 합니다.',
                );
            } else if (!serverMsg || serverMsg === 'Internal Server Error') {
                setErr('프로젝트 역할 조정에 실패했습니다.');
            } else {
                setErr(serverMsg);
            }
        }
    };

    // ✅ 리스트에서 역할 변경 (기존 프로젝트 멤버의 role 변경)
    const handleChangeRole = async (m: Member, nextRole: string) => {
        if (!canManage) return;

        // OWNER / MANAGER 로 변경은 여기서 허용하지 않음
        if (HIGH_PRIV_PROJECT_ROLES.includes(nextRole as any)) return;

        try {
            setErr(null);
            await updateMemberRole(projectId, m.userId, nextRole as Member['role']);
            await reload();
        } catch (e: any) {
            const serverMsg = e?.response?.data?.error?.message;
            setErr(serverMsg ?? '역할 변경에 실패했습니다.');
        }
    };

    // ✅ 멤버 제거
    const handleRemove = async (m: Member) => {
        if (!canManage) return;
        if (!window.confirm('이 멤버를 프로젝트에서 제거하시겠습니까?')) return;

        const isOwnerRow = m.role === 'PROJECT_OWNER';
        const isManagerRow = m.role === 'PROJECT_MANAGER';
        const isSelf = auth?.me && m.userId === auth.me.id;

        let canDeleteThis = false;

        if (!isSelf) {
            if (isOwnerInThisProject) {
                // OWNER: OWNER row 제외, 나머지는 삭제 가능 (PM 포함)
                canDeleteThis = !isOwnerRow;
            } else if (isManagerInThisProject) {
                // PM: OWNER / PM row는 삭제 불가, 나머지 삭제 가능
                canDeleteThis = !isOwnerRow && !isManagerRow;
            }
        }

        if (!canDeleteThis) return;

        try {
            setErr(null);
            await removeMember(projectId, m.userId);
            await reload();
        } catch (e: any) {
            const serverMsg = e?.response?.data?.error?.message;
            setErr(serverMsg ?? '멤버 제거에 실패했습니다.');
        }
    };

    const deleteButtonWidthClass = 'w-[56px]'; // "제거" 버튼 폭 맞추기용

    // 모달 열기/닫기
    const openUserPicker = () => {
        if (!canManage) return;
        setShowUserPicker(true);
    };

    const closeUserPicker = () => {
        setShowUserPicker(false);
        setUserFilter('');
    };

    const handleSelectCompanyUser = (u: CompanyUserLite) => {
        setEmail(u.email);
        closeUserPicker();
    };

    // 초대 버튼 활성화 조건
    const canSubmitInvite = canManage && email.trim().length > 0 && role && role.length > 0;

    return (
        <div className="space-y-4">
            {err && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{err}</div>
            )}

            <div className="grid gap-4 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
                {/* 왼쪽: 멤버 리스트 카드 */}
                <div className="rounded-2xl border bg-white">
                    <div className="border-b px-4 py-3 text-sm font-medium">프로젝트 멤버</div>
                    <div className="p-4">
                        {items.length === 0 ? (
                            <p className="py-6 text-center text-sm text-gray-500">아직 등록된 멤버가 없습니다.</p>
                        ) : (
                            <ul className="space-y-2">
                                {items.map((m) => {
                                    const isHighPrivMember = HIGH_PRIV_PROJECT_ROLES.includes(m.role as any);
                                    const isSelf = auth?.me && m.userId === auth.me.id;

                                    const isOwnerRow = m.role === 'PROJECT_OWNER';
                                    const isManagerRow = m.role === 'PROJECT_MANAGER';

                                    let canDeleteThis = false;
                                    if (!isSelf) {
                                        if (isOwnerInThisProject) {
                                            // OWNER: OWNER row 제외 모두 삭제 가능 (PM 포함)
                                            canDeleteThis = !isOwnerRow;
                                        } else if (isManagerInThisProject) {
                                            // PM: OWNER/PM row 제외 삭제 가능
                                            canDeleteThis = !isOwnerRow && !isManagerRow;
                                        }
                                    }

                                    return (
                                        <li
                                            key={m.userId}
                                            className="flex items-center justify-between rounded-xl border px-3 py-2"
                                        >
                                            {/* 왼쪽: 유저 정보 */}
                                            <div className="min-w-0">
                                                <div className="truncate text-sm font-medium text-gray-900">
                                                    {m.user?.name ?? m.userId}
                                                </div>
                                                <div className="truncate text-xs text-gray-500">{m.user?.email}</div>
                                            </div>

                                            {/* 오른쪽: 역할 + 삭제 버튼 */}
                                            <div className="flex items-center gap-2 pl-4">
                                                {/* 역할 표시/변경 */}
                                                {isHighPrivMember || !canManage ? (
                                                    <span className="w-[170px] rounded-lg border bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-700">
                                                        {m.role}
                                                    </span>
                                                ) : (
                                                    <select
                                                        className="w-[170px] rounded-xl border px-3 py-1.5 text-xs appearance-none focus:outline-none focus:ring-1 focus:ring-sky-500"
                                                        value={m.role}
                                                        onChange={(e) => handleChangeRole(m, e.target.value)}
                                                    >
                                                        {projectRoles.length === 0 && (
                                                            <option value={m.role}>{m.role}</option>
                                                        )}

                                                        {projectRoles
                                                            .filter(
                                                                (r) => !HIGH_PRIV_PROJECT_ROLES.includes(r.name as any),
                                                            )
                                                            .map((r) => (
                                                                <option key={r.id} value={r.name}>
                                                                    {r.name}
                                                                </option>
                                                            ))}
                                                    </select>
                                                )}

                                                {/* 삭제 버튼 (레이아웃 유지용 폭 고정) */}
                                                {canDeleteThis ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemove(m)}
                                                        className={`${deleteButtonWidthClass} inline-flex items-center justify-center rounded-lg border px-2 py-1 text-xs text-gray-700 hover:bg-gray-50`}
                                                    >
                                                        제거
                                                    </button>
                                                ) : (
                                                    <span className={`${deleteButtonWidthClass} inline-block`} />
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>

                {/* 오른쪽: 프로젝트 멤버 초대 카드 */}
                <div className="rounded-2xl border bg-white">
                    <div className="border-b px-4 py-3 text-sm font-medium">프로젝트 멤버 초대</div>
                    <form className="flex flex-col gap-3 p-4" onSubmit={handleInviteOrAdjust}>
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center justify-between">
                                <label className="text-xs font-medium text-gray-600">이메일 (CompanyUser)</label>
                                {canManage && (
                                    <button
                                        type="button"
                                        onClick={openUserPicker}
                                        className="text-xs rounded-lg border px-2 py-1 text-gray-700 hover:bg-gray-50"
                                    >
                                        목록
                                    </button>
                                )}
                            </div>
                            {/* 입력 + 자동완성 */}
                            <div className="relative">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        setShowSuggestions(true);
                                    }}
                                    onFocus={() => {
                                        if (canManage) {
                                            setShowSuggestions(true);
                                        }
                                    }}
                                    onBlur={() => {
                                        setTimeout(() => setShowSuggestions(false), 100);
                                    }}
                                    placeholder="example@company.com"
                                    className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-sky-500"
                                    disabled={!canManage}
                                />
                                {showSuggestions && filteredSuggestions.length > 0 && (
                                    <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-xl border bg-white text-xs shadow-lg">
                                        {filteredSuggestions.map((u) => (
                                            <li key={u.id}>
                                                <button
                                                    type="button"
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        setEmail(u.email);
                                                        setShowSuggestions(false);
                                                    }}
                                                    className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-gray-50"
                                                >
                                                    <span className="font-medium text-gray-900">
                                                        {u.name ?? '(이름 없음)'}
                                                    </span>
                                                    <span className="text-gray-500">{u.email}</span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-medium text-gray-600">프로젝트 역할(Role)</label>
                            <select
                                className="w-full rounded-xl border px-3 py-1.5 text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-sky-500"
                                value={role}
                                onChange={(e) => setRole(e.target.value)}
                                disabled={!canManage}
                            >
                                {projectRoles.length === 0 && <option value="">역할을 선택해 주세요</option>}

                                <option value="" disabled>
                                    역할을 선택해 주세요
                                </option>

                                {projectRoles
                                    .filter((r) => !HIGH_PRIV_PROJECT_ROLES.includes(r.name as any))
                                    .map((r) => (
                                        <option key={r.id} value={r.name}>
                                            {r.name}
                                        </option>
                                    ))}
                            </select>
                        </div>

                        <button
                            type="submit"
                            disabled={!canSubmitInvite}
                            className="mt-1 inline-flex items-center justify-center rounded-xl bg-orange-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            초대
                        </button>
                        {!canManage && (
                            <p className="text-xs text-gray-500">
                                프로젝트 역할 조정은 PROJECT_OWNER 또는 PROJECT_MANAGER만 가능합니다.
                            </p>
                        )}
                    </form>
                </div>
            </div>

            {/* 회사 사용자 선택 모달 */}
            {showUserPicker && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
                    <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
                        <div className="flex items-center justify-between border-b px-4 py-3">
                            <h2 className="text-sm font-medium">회사 사용자 선택</h2>
                            <button
                                type="button"
                                onClick={closeUserPicker}
                                className="text-xs text-gray-500 hover:text-gray-700"
                            >
                                닫기
                            </button>
                        </div>
                        <div className="p-4 space-y-3">
                            <input
                                type="text"
                                value={userFilter}
                                onChange={(e) => setUserFilter(e.target.value)}
                                placeholder="이름 또는 이메일로 검색"
                                className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-sky-500"
                            />
                            {companyUsersLoading ? (
                                <p className="py-4 text-center text-xs text-gray-500">
                                    회사 사용자 목록을 불러오는 중입니다...
                                </p>
                            ) : companyUsersErr ? (
                                <p className="py-4 text-center text-xs text-red-500">{companyUsersErr}</p>
                            ) : filteredCompanyUsers.length === 0 ? (
                                <p className="py-4 text-center text-xs text-gray-500">표시할 사용자가 없습니다.</p>
                            ) : (
                                <ul className="max-h-64 space-y-1 overflow-y-auto">
                                    {filteredCompanyUsers.map((u) => (
                                        <li key={u.id}>
                                            <button
                                                type="button"
                                                onClick={() => handleSelectCompanyUser(u)}
                                                className="flex w-full flex-col items-start rounded-xl border px-3 py-2 text-left text-xs hover:bg-gray-50"
                                            >
                                                <span className="font-medium text-gray-900">
                                                    {u.name ?? '(이름 없음)'}
                                                </span>
                                                <span className="text-gray-500">{u.email}</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MembersTab;
